import { Filter } from 'bad-words';

// Create a new filter instance
const filter = new Filter();

// Add additional words that might be inappropriate for high school students
const additionalWords = [
  // Add any additional words specific to your context
  // These are examples - you can customize based on your needs
  'stupid', 'idiot', 'moron', 'dumb', 'loser'
];

// Add the additional words to the filter
filter.addWords(...additionalWords);

// Custom whitelist for words that might be flagged but are acceptable
const whitelist = [
  // Add words that should be allowed even if flagged
  // Example: 'class' might be flagged but is educational
  'class', 'assignment', 'essay', 'balls', 'stupid', 'idiot', 'moron', 'dumb', 'loser','hell'
];

// Remove whitelisted words from the filter
filter.removeWords(...whitelist);
// console.log('Profanity filter initialized with custom words:');
// filter.list.forEach(word => console.log(` - ${word}` ));
/**
 * Check if text contains profanity
 * @param {string} text - The text to check
 * @returns {boolean} - True if profanity is found
 */
export const containsProfanity = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }
  return filter.isProfane(text);
};

/**
 * Clean profanity from text by replacing with asterisks
 * @param {string} text - The text to clean
 * @returns {string} - The cleaned text
 */
export const cleanProfanity = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }
  return filter.clean(text);
};

/**
 * Get a list of profane words found in the text
 * @param {string} text - The text to analyze
 * @returns {Array} - Array of profane words found
 */
export const getProfaneWords = (text) => {
  if (!text || typeof text !== 'string') {
    return [];
  }
  
  const words = text.toLowerCase().split(/\s+/);
  const profaneWords = [];
  
  words.forEach(word => {
    // Remove punctuation for checking
    const cleanWord = word.replace(/[^\w]/g, '');
    if (cleanWord && filter.isProfane(cleanWord)) {
      profaneWords.push(word);
    }
  });
  
  return [...new Set(profaneWords)]; // Remove duplicates
};

/**
 * Validate text input for profanity with detailed feedback
 * @param {string} text - The text to validate
 * @returns {Object} - Validation result with details
 */
export const validateTextForProfanity = (text) => {
  if (!text || typeof text !== 'string') {
    return {
      isValid: true,
      hasProfanity: false,
      profaneWords: [],
      cleanedText: text,
      message: ''
    };
  }

  const hasProfanity = containsProfanity(text);
  const profaneWords = getProfaneWords(text);
  const cleanedText = cleanProfanity(text);

  return {
    isValid: !hasProfanity,
    hasProfanity,
    profaneWords,
    cleanedText,
    message: hasProfanity 
      ? `Please remove inappropriate language: ${profaneWords.join(', ')}` 
      : 'Text is appropriate'
  };
};

/**
 * Real-time text validation for form inputs
 * @param {string} text - The text to validate
 * @param {Function} onValidation - Callback function with validation results
 */
export const validateTextRealTime = (text, onValidation) => {
  const validation = validateTextForProfanity(text);
  
  if (onValidation && typeof onValidation === 'function') {
    onValidation(validation);
  }
  
  return validation;
};

// Export the filter instance for advanced usage
export { filter };

// Default export
export default {
  containsProfanity,
  cleanProfanity,
  getProfaneWords,
  validateTextForProfanity,
  validateTextRealTime,
  filter
};
